import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const ROLES = ["ADMIN", "VALIDATEUR", "REDACTEUR"];

export default function Administration() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [types, setTypes] = useState([]);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [newUser, setNewUser] = useState({ email: "", nom: "", role: "REDACTEUR", site_rattachement: "" });
  const [siteForm, setSiteForm] = useState({ code: "", nom: "", adresse: "" });

  const loadAll = () => {
    api.get("/utilisateurs").then((res) => setUsers(res.data));
    api.get("/sites").then((res) => setSites(res.data));
    api.get("/types-operations").then((res) => setTypes(res.data));
  };

  useEffect(() => {
    if (user?.Role !== "ADMIN") return;
    loadAll();
  }, [user]);

  if (user?.Role !== "ADMIN") {
    return (
      <div className="p-6 text-sm text-slate-500" data-testid="admin-access-denied">
        {t("common.access_denied")}
      </div>
    );
  }

  const updateUserField = async (userId, field, value) => {
    await api.put(`/utilisateurs/${userId}`, { [field]: value });
    setUsers(users.map((u) => (u.User_ID === userId ? { ...u, [field === "role" ? "Role" : "Site_Rattachement"]: value } : u)));
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/utilisateurs", newUser);
      setUsers([...users, res.data]);
      setUserDialogOpen(false);
      setNewUser({ email: "", nom: "", role: "REDACTEUR", site_rattachement: "" });
      toast.success(t("common.user_created"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm(t("common.confirm_delete_user"))) return;
    try {
      await api.delete(`/utilisateurs/${userId}`);
      setUsers(users.filter((u) => u.User_ID !== userId));
      toast.success(t("common.user_deleted"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const createSite = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/sites", siteForm);
      setSites([...sites, res.data]);
      setSiteDialogOpen(false);
      setSiteForm({ code: "", nom: "", adresse: "" });
      toast.success(t("common.site_created"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const saveEditSite = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/sites/${editingSite.Site_ID}`, { nom: editingSite.Nom, adresse: editingSite.Adresse });
      setSites(sites.map((s) => (s.Site_ID === editingSite.Site_ID ? editingSite : s)));
      setEditingSite(null);
      toast.success(t("common.save"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteSite = async (siteId) => {
    if (!window.confirm(t("common.confirm_delete_site"))) return;
    try {
      await api.delete(`/sites/${siteId}`);
      setSites(sites.filter((s) => s.Site_ID !== siteId));
      toast.success(t("common.site_deleted"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-8" data-testid="administration-view">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{t("nav.admin")}</h1>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Utilisateurs</h2>
          <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="btn-add-user">
                <Plus className="w-4 h-4" />
                {t("common.add_user")}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="dialog-add-user">
              <DialogHeader>
                <DialogTitle>{t("common.add_user")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={createUser} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>{t("common.email")}</Label>
                  <Input data-testid="input-user-email" type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("common.name")}</Label>
                  <Input data-testid="input-user-nom" required value={newUser.nom} onChange={(e) => setNewUser({ ...newUser, nom: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("common.role")}</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                    <SelectTrigger data-testid="select-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>
                          {t(`roles.${r}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("common.site")}</Label>
                  <Select value={newUser.site_rattachement} onValueChange={(v) => setNewUser({ ...newUser, site_rattachement: v })}>
                    <SelectTrigger data-testid="select-user-site">
                      <SelectValue placeholder={t("common.site")} />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((s) => (
                        <SelectItem key={s.Site_ID} value={s.Site_ID}>
                          {s.Nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="btn-submit-user">
                    {t("common.create")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-2">
          {users.map((u) => (
            <div
              key={u.User_ID}
              className="flex items-center justify-between gap-2 flex-wrap bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3"
              data-testid={`user-row-${u.User_ID}`}
            >
              <div>
                <p className="font-medium text-slate-800 dark:text-slate-200 text-sm">{u.Nom}</p>
                <p className="text-xs text-slate-500">{u.Email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={u.Role} onValueChange={(v) => updateUserField(u.User_ID, "role", v)}>
                  <SelectTrigger data-testid={`role-select-${u.User_ID}`} className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {t(`roles.${r}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={u.Site_Rattachement || ""} onValueChange={(v) => updateUserField(u.User_ID, "site_rattachement", v)}>
                  <SelectTrigger data-testid={`site-select-${u.User_ID}`} className="w-36">
                    <SelectValue placeholder={t("common.site")} />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => (
                      <SelectItem key={s.Site_ID} value={s.Site_ID}>
                        {s.Nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" data-testid={`btn-delete-user-${u.User_ID}`} onClick={() => deleteUser(u.User_ID)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{t("common.site")}s</h2>
          <Dialog open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="btn-add-site">
                <Plus className="w-4 h-4" />
                {t("common.add_site")}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="dialog-add-site">
              <DialogHeader>
                <DialogTitle>{t("common.add_site")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={createSite} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Code</Label>
                  <Input data-testid="input-site-code" required value={siteForm.code} onChange={(e) => setSiteForm({ ...siteForm, code: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("common.name")}</Label>
                  <Input data-testid="input-site-nom" required value={siteForm.nom} onChange={(e) => setSiteForm({ ...siteForm, nom: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("common.address")}</Label>
                  <Input data-testid="input-site-adresse" value={siteForm.adresse} onChange={(e) => setSiteForm({ ...siteForm, adresse: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="btn-submit-site">
                    {t("common.create")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {sites.map((s) => (
            <div key={s.Site_ID} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-sm flex items-center justify-between" data-testid={`site-row-${s.Site_ID}`}>
              <div>
                <p className="font-medium text-slate-800 dark:text-slate-200">{s.Nom}</p>
                <p className="text-xs font-mono text-slate-500">{s.Code}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" data-testid={`btn-edit-site-${s.Site_ID}`} onClick={() => setEditingSite({ ...s })}>
                  {t("common.edit")}
                </Button>
                <Button variant="ghost" size="icon" data-testid={`btn-delete-site-${s.Site_ID}`} onClick={() => deleteSite(s.Site_ID)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Dialog open={!!editingSite} onOpenChange={(open) => !open && setEditingSite(null)}>
        <DialogContent data-testid="dialog-edit-site">
          <DialogHeader>
            <DialogTitle>{t("common.edit")}</DialogTitle>
          </DialogHeader>
          {editingSite && (
            <form onSubmit={saveEditSite} className="space-y-3">
              <div className="space-y-1.5">
                <Label>{t("common.name")}</Label>
                <Input data-testid="input-edit-site-nom" value={editingSite.Nom} onChange={(e) => setEditingSite({ ...editingSite, Nom: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.address")}</Label>
                <Input data-testid="input-edit-site-adresse" value={editingSite.Adresse || ""} onChange={(e) => setEditingSite({ ...editingSite, Adresse: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="btn-save-edit-site">
                  {t("common.save")}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <section>
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">{t("common.operation")}s</h2>
        <div className="grid sm:grid-cols-2 gap-2">
          {types.map((o) => (
            <div key={o.Type_ID} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-sm" data-testid={`type-row-${o.Type_ID}`}>
              <p className="font-medium text-slate-800 dark:text-slate-200">{o.Nom}</p>
              <p className="text-xs text-slate-500">{o.Description}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
